import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GenerationProgress from '../../Wizard/PlanGeneration/GenerationProgress';
import MacroPreview from '../../Wizard/PlanGeneration/MacroPreview';
import TipsCarousel from '../../Wizard/PlanGeneration/TipsCarousel';
import { selectTips } from '../../Wizard/PlanGeneration/tips';

function ModificationBanner({ requestText }) {
  const { t } = useTranslation();
  const text = String(requestText || '').trim();
  if (!text) return null;

  return (
    <div className="p-4 rounded-lg border border-indigo-100 bg-indigo-50 text-left mb-6 w-full">
      <div className="font-bold text-indigo-900">{t('plan.generating')}</div>
      <div className="text-sm text-indigo-900 mt-1">
        {t('plan.integrating_changes')} <span className="font-semibold">"{text}"</span>
      </div>
    </div>
  );
}

export default function PlanGenerationScreen({ planData, lastModRequest }) {
  const { t, i18n } = useTranslation();

  const status = String(planData?.status || '').trim();
  const isModRegeneration = status === 'generating';
  
  // Statuses for full generation flow (Wizard or regenerate_plan.js)
  const isWorkouts = status === 'generating_workouts';
  const isAudit = status === 'generating_audit';
  
  // Texts & Duration
  let statusLabel = t('wizard.generation.step1'); // "Designing your macro-cycle..."
  let duration = 60;

  if (isWorkouts || isAudit) {
      statusLabel = t('wizard.generation.step2'); // "Crafting every single workout..."
      duration = 180;
  }

  // If simple modification, use generic text
  if (isModRegeneration) {
      statusLabel = t('plan.generation.updating_title');
      duration = 45;
  }

  const tips = useMemo(() => {
    const profile = planData?.profile || {};
    const goal = planData?.goal || {};
    // Extract sport from planData or profile
    const sport = planData?.sport || profile?.sport || 'run';
    
    return selectTips({
      sport: sport,
      personaId: profile?.persona_id || '',
      goalPrimary: profile?.goal_primary || '',
      eventDistanceType: goal?.event_distance_type || ''
    });
  }, [planData]);

  const planJson = planData?.plan;

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 bg-white shadow-lg rounded-lg mt-2 md:mt-10 flex flex-col items-center justify-center min-h-[500px]">
        
        {isModRegeneration ? (
          <ModificationBanner requestText={lastModRequest} />
        ) : null}

        {/* 1) Progress Bar */}
        <div className="w-full mb-6">
           <GenerationProgress
             durationSeconds={duration}
             status={statusLabel}
             label={t('wizard.generation.wait_hint')}
           />
        </div>

        {/* 2) Tips Carousel - Hide during workout generation to show preview instead? 
           Wizard.js hides it: {!isWorkouts && (...)} 
           We also hide it if we are in modification mode to keep it simple, or keep it?
           Let's match Wizard.js logic: Show tips during Step 1 (macro), hide during Step 2 (workouts) to show preview.
        */}
        {!isWorkouts && !isAudit && (
            <div className="w-full animate-fade-in mb-6">
               <TipsCarousel
                 tips={tips}
                 locale={i18n.language}
               />
            </div>
        )}

        {/* 3) Macro Preview */}
        {planJson ? (
            <div className="w-full mt-4 animate-fade-in-up">
                <MacroPreview planJson={planJson} />
            </div>
        ) : null}
    </div>
  );
}
